import { Loader2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { BankAccountScreen } from '@/features/workflows/components/simple/preview-screens';
import { toast } from '@/lib/notify';

import { useVerifyStep } from '../../api/useVerifyStep';
import type {
  SessionDetailDto,
  SessionStepApiBindingDto,
  SessionStepDto,
} from '../../schemas/sessionSchemas';
import { extractReason } from '../pan/pan.utils';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';
import {
  CHECK_FAILED_TOAST,
  RETRY_EXHAUSTED_NOTE,
  tryAnotherMethodNote,
} from '../stepFallbackNotes';
import { BankDevSimulator } from './BankDevSimulator';
import { isValidAccountNumber, isValidIfsc } from './bank.utils';

// Fallback binding for legacy sessions whose snapshot carries no bindings —
// submits without a catalogueEntryId, letting the backend run its own chain.
const DEFAULT_BINDING: SessionStepApiBindingDto = {
  catalogueEntryId: '',
  path: '/api/v1/Bank/account/verify',
  operationId: 'Post_Bank_PennyDrop_Basic',
  summary: 'Penny Drop - Basic',
  priority: 1,
};

/**
 * Runs one Bank Account Verification step in a live session. The applicant enters
 * their account number + IFSC; the backend verifier injects consent + isPennyDrop
 * and calls the selected method (Penny Drop / Penniless, Basic / Advance). On a
 * technical failure the runner advances to the next enabled method (visible
 * priority fallback), same as the PAN / Aadhaar / Face runners.
 */
export function BankStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { mutate, isPending } = useVerifyStep(session.id);

  const bindings = useMemo(() => {
    const b = step.apiBindings.slice().sort((x, y) => x.priority - y.priority);
    return b.length > 0 ? b : [DEFAULT_BINDING];
  }, [step.apiBindings]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [accountNumber, setAccountNumber] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const active = bindings[activeIdx] ?? bindings[0] ?? DEFAULT_BINDING;

  const accountValid = isValidAccountNumber(accountNumber);
  const ifscValid = isValidIfsc(ifsc);
  const ready = accountValid && ifscValid;

  if (expired || session.isExpired) {
    return <SessionExpiredScreen />;
  }

  // Business reject (step Failed) → terminal rejection screen (inactive account
  // or a Reject risk-rule / condition). A reject ends the session — no retry.
  if (step.status === 'Failed') {
    return <FailureScreen reason={extractReason(step.resultJson)} />;
  }

  function buildInput(): Record<string, unknown> {
    // Applicant data only — the backend verifier's NormalizeInput injects the
    // consent trio + isPennyDrop and the name-match reference comes from an
    // earlier PAN/Aadhaar step (engine-injected). Field names match the gateway
    // request (accountNumber / ifsc).
    return { accountNumber: accountNumber.trim(), ifsc: ifsc.trim().toUpperCase() };
  }

  function handleSubmit() {
    if (!ready) return;
    submitInput(buildInput());
  }

  // DEV: one-click scenario — submits a `test_case` (backend BankMockResponses
  // returns the canned result) through the SAME flow so rules/fallback run.
  function runTestCase(testCase: string) {
    submitInput({ accountNumber: '', ifsc: '', test_case: testCase });
  }

  function submitInput(input: Record<string, unknown>) {
    mutate(
      {
        stepId: step.id,
        input,
        catalogueEntryId: active.catalogueEntryId || undefined,
      },
      {
        onSuccess: (data) => {
          const updated = data.steps.find((s) => s.id === step.id);
          // Provisional shared-link mode: fresh step ids won't match — the entry
          // screen navigates to the real run on success. Nothing to do here.
          if (!updated) return;
          const status = updated?.status;
          if (status === 'Passed' || status === 'Review' || status === 'Failed') {
            // Final verdict — the parent advances / shows the failure screen.
            return;
          }
          // status Pending → technical provider failure → visible fallback.
          const nextBinding = bindings[activeIdx + 1];
          if (nextBinding) {
            // Deliberately does NOT name the two methods (`active.summary` /
            // `nextBinding.summary`). Those are internal product names — "Penny Drop
            // - Basic", "Penniless - Advance" — and printing them here told the
            // applicant which provider strategy we fall back to, which they can't
            // act on. What they need to know is that we are retrying.
            setFallbackNote(tryAnotherMethodNote());
            setActiveIdx(activeIdx + 1);
          } else {
            toast.error(CHECK_FAILED_TOAST);
            setFallbackNote(RETRY_EXHAUSTED_NOTE);
            setActiveIdx(0);
          }
        },
        onError: (error) => {
          if (isSessionExpiredError(error)) {
            setExpired(true);
            return;
          }
          toast.error('Could not reach the server. Please try again.');
        },
      },
    );
  }

  return (
    // `flex-1` + `mt-auto` on Continue below — the shared pattern that keeps the
    // button in the same place on every step (see the other runners). This one was
    // sizing to its content, so its Continue sat higher than the rest.
    <div className="flex flex-1 flex-col gap-4">
      {/* DEV-ONLY: one-click bank scenarios (backend BankMockResponses) to exercise
          the step's Risk / Advanced Rules. Stripped from prod. */}
      {import.meta.env.DEV && <BankDevSimulator onRun={runTestCase} isPending={isPending} />}

      {fallbackNote && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
          {fallbackNote}
        </div>
      )}

      <BankAccountScreen
        pennyDrop={/penny drop/i.test(active.summary)}
        values={{ accountNumber, ifsc }}
        onChange={(patch) => {
          if (patch.accountNumber !== undefined) setAccountNumber(patch.accountNumber);
          if (patch.ifsc !== undefined) setIfsc(patch.ifsc);
        }}
        accountError={
          accountNumber.length > 0 && !accountValid
            ? 'Enter a valid 9–18 digit account number.'
            : undefined
        }
        ifscError={
          ifsc.length > 0 && !ifscValid ? 'Enter a valid IFSC (e.g. SBIN0001234).' : undefined
        }
      />

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isPending || !ready}
        style={{
          backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
          color: 'var(--brand-btn-text, #ffffff)',
          borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
          borderRadius: 'var(--brand-btn-radius, 0.75rem)',
        }}
        className="mt-auto flex w-full items-center justify-center gap-2 border py-3 text-[15px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
      >
        {isPending && <Loader2 size={15} className="animate-spin" />}
        Continue
      </button>
    </div>
  );
}

// Shown when a Bank step is rejected (business Failed) — e.g. an inactive account
// or a Reject risk-rule fired. Terminal.
function FailureScreen({ reason }: { reason?: string | null }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#DC2626]">
        <X size={60} strokeWidth={3} color="white" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#DC2626]">
        Bank verification failed
      </h2>
      <p className="text-[length:var(--text-14)] text-[#666666]">
        {reason ?? 'Your bank account could not be verified. This session has been rejected.'}
      </p>
    </div>
  );
}
